import { Badge, Button, Select, Separator, Text } from '@radix-ui/themes';
import { toast } from 'react-hot-toast';
import { Overlay } from '@/pages/home/ui/overaly';
import { SkuList } from '@/pages/home/ui/sku-list';
import {
  initCollection,
  isStartDisabled,
  resetCollection,
  selectIsControlsDisabled,
  setDatasetType,
  setMode,
  setZone,
} from '@/entities/collection';
import {
  useCompleteMutation,
  useCreateCollectionMutation,
  useZonesQuery,
} from '@/shared/api';
import { CollectionMode, DatasetType } from '@/shared/api/types';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

const collectionModes = [
  {
    value: 'single',
    label: 'Single-SKU',
  },
  {
    value: 'multi',
    label: 'Multi-SKU',
  },
];

const datasetTypes = [
  {
    value: 'validation',
    label: 'Evaluation dataset',
  },
  {
    value: 'training',
    label: 'Training dataset',
  },
];

export function Sidebar() {
  const isDisabled = useAppSelector(isStartDisabled);
  const dataset = useAppSelector((state) => state.collection.dataset);
  const mode = useAppSelector((state) => state.collection.mode);
  const zoneId = useAppSelector((state) => state.collection.zoneId);
  const collectionId = useAppSelector((state) => state.collection.collectionId);
  const [createCollection, { isLoading }] = useCreateCollectionMutation();
  const [completeCollection, completeResult] = useCompleteMutation();
  const { zones } = useZonesQuery(undefined, {
    selectFromResult: ({ data, ...ops }) => ({ ...ops, zones: data?.results }),
  });
  const isControlsDisabled = useAppSelector(selectIsControlsDisabled);

  const dispatch = useAppDispatch();

  return (
    <aside className="p-4 h-full flex flex-col">
      <div className="relative">
        {!!collectionId && <Overlay />}

        <Text as="p" className="font-medium mb-1">
          Collection Mode
        </Text>
        <Select.Root
          value={(mode || '') as CollectionMode}
          onValueChange={(value) => {
            dispatch(setMode(value as CollectionMode));
          }}
        >
          <Select.Trigger placeholder="Select..." className="w-full mb-2" />
          <Select.Content>
            {collectionModes.map((mode) => (
              <Select.Item value={mode.value} key={mode.value}>
                {mode.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
        <Text as="p" className="font-medium mb-1">
          Dataset Mode
        </Text>
        <Select.Root
          value={(dataset || '') as DatasetType}
          onValueChange={(value) => {
            dispatch(setDatasetType(value as DatasetType));
          }}
        >
          <Select.Trigger placeholder="Select..." className="w-full mb-2" />
          <Select.Content>
            {datasetTypes.map((type) => (
              <Select.Item value={type.value} key={type.value}>
                {type.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
        <Text as="p" className="font-medium mb-1">
          Zone
        </Text>
        <Select.Root
          value={zoneId || ''}
          onValueChange={(value) => {
            dispatch(setZone(value));
          }}
        >
          <Select.Trigger placeholder="Select..." className="w-full" />
          <Select.Content>
            {zones?.map((zone) => (
              <Select.Item
                value={zone.id}
                key={zone.description}
                disabled={zone.camera_id === null}
              >
                {zone.description}{' '}
                {zone.camera_id === null && (
                  <Badge color="gray" size="1" className="opacity-50">
                    No camera
                  </Badge>
                )}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </div>

      <Separator size="4" className="my-4" />

      <SkuList collectionId={collectionId} />
      {!collectionId ? (
        <Button
          size="3"
          disabled={isLoading || isDisabled}
          onClick={() => {
            if (!dataset || !mode || !zoneId)
              return toast.error('Please select dataset, mode and zone');

            createCollection({
              name: `Collection_${new Date().toISOString()}`,
              dataset_type: dataset,
              mode: mode,
              zone_id: zoneId,
            })
              .unwrap()
              .then((result) => {
                dispatch(
                  initCollection({
                    id: result.result.id,
                    dataset_type: dataset,
                    mode: mode,
                    zone_id: zoneId,
                  })
                );
              });
          }}
        >
          Start
        </Button>
      ) : (
        <Button
          size="3"
          variant="soft"
          loading={completeResult.isLoading}
          disabled={isControlsDisabled}
          onClick={() => {
            if (!dataset || !mode) return;

            completeCollection({
              collection_id: collectionId,
            })
              .unwrap()
              .then(() => {
                dispatch(resetCollection());
                toast.success('The collection session is finished');
              });
          }}
        >
          Finish
        </Button>
      )}
    </aside>
  );
}
